import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useData } from '../../data/hooks';
import { countOutbox } from '../../data/outbox';
import { COPY } from '../auth/authErrors';
import { useSession } from '../auth/session';

export type SignOutKind = 'this_device' | 'everywhere';

export function unsyncedTitle(n: number): string {
  return `${n} ${n === 1 ? "change hasn't" : "changes haven't"} synced yet. Sign out anyway?`;
}

/** Push first; ask if changes remain; then (revoke everywhere if asked,) wipe the device DB and end the session. */
export function useSignOut() {
  const session = useSession();
  const { db, sync, wipeLocalData } = useData();
  const navigate = useNavigate();
  const [unsynced, setUnsynced] = useState<{ kind: SignOutKind; pending: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function finish(kind: SignOutKind): Promise<void> {
    setUnsynced(null);
    setBusy(true);
    setError(null);
    try {
      if (kind === 'everywhere') await session.revokeAllSessions(); // throws offline: nothing is wiped
      await wipeLocalData();
      await session.signOut();
      navigate('/sign-in', { replace: true });
    } catch {
      setError(COPY.offlineTryAgain);
    } finally {
      setBusy(false);
    }
  }

  async function start(kind: SignOutKind): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await sync.syncNow();
    } catch {
      // offline or server down: the count below decides
    }
    const pending = await countOutbox(db);
    setBusy(false);
    if (pending > 0) setUnsynced({ kind, pending });
    else await finish(kind);
  }

  return { start, finish, unsynced, cancel: () => setUnsynced(null), busy, error };
}
